/**
 * Session health indicator — pure, host-agnostic.
 *
 * JAISIU-4089 — implement a small persistent health pill that surfaces
 * the operator's view of the connection to the local gateway. The
 * verdict is a tri-state:
 *
 *   - "online"   — the gateway is reachable AND we have not seen a
 *                  recent transient failure. Steady state.
 *   - "degraded" — the gateway is reachable but one or more recent
 *                  calls failed OR the optimistic outbox still has
 *                  pending items waiting on the next round-trip. The
 *                  pill surfaces this so the operator knows a
 *                  background reconciliation is in progress and that
 *                  some of their input may not have reached the
 *                  server yet.
 *   - "offline"  — the websocket is closed (or the very first
 *                  connection attempt has not yet succeeded). The
 *                  chat composer is disabled; pending optimistic
 *                  messages stay visible but cannot be retried until
 *                  the socket re-opens.
 *
 * The computation is pure: callers pass a `SessionHealthInput` snapshot
 * and receive a `SessionHealth` verdict + a short human-readable reason.
 * The companion `startSessionHealthMonitor` in
 * `chat-session-health-monitor.ts` polls on an interval and stamps the
 * verdict onto the host, but this module does NOT touch the DOM or
 * any global state — it stays unit-testable.
 *
 * The thresholds (`RECENT_FAILURE_WINDOW_MS`, `DEGRADED_FAILURE_COUNT`,
 * `OUTBOX_DEGRADED_COUNT`) are intentionally conservative: a single
 * rate-limited retry should NOT flip the pill to "degraded", but a
 * sustained burst of 3+ failures within 30s, or any pending outbox
 * item, will. Tuning is done at the call site by passing a custom
 * `SessionHealthThresholds` object.
 */

export type SessionHealth = "online" | "degraded" | "offline";

/**
 * A single recorded failure event. Timestamps are wall-clock ms
 * (Date.now()), matching the rest of the chat subsystem.
 */
export interface SessionHealthFailure {
  /** Wall-clock ms when the failure was recorded. */
  at: number;
  /** Short human-readable reason (e.g. error code). */
  reason: string;
}

/**
 * Snapshot of host state passed to `computeSessionHealth`. Designed
 * to be collected cheaply on every poll — no synchronous network
 * calls, no DOM reads. The monitor in `chat-session-health-monitor.ts`
 * gathers these from the host's existing reactive properties.
 */
export interface SessionHealthInput {
  /** True when the websocket is currently open. */
  connected: boolean;
  /** True when the host has received its first `hello` from the gateway. */
  hasHello: boolean;
  /** Pending optimistic outbox items waiting on a round-trip. */
  pendingOutboxCount: number;
  /** Recent failures, oldest first. The monitor trims the buffer. */
  recentFailures: readonly SessionHealthFailure[];
  /** Current wall-clock ms (Date.now()). */
  nowMs: number;
}

/**
 * Verdict returned by `computeSessionHealth`. The `reason` field is a
 * short operator-facing string suitable for the indicator's `title`
 * attribute; the indicator itself shows only the status word.
 */
export interface SessionHealthVerdict {
  kind: SessionHealth;
  reason: string;
}

/**
 * Tuning knobs. Defaults match the values in the chat subsystem's
 * existing retry / backoff logic so a single transient 429 does not
 * flip the pill, but a sustained burst will.
 */
export interface SessionHealthThresholds {
  /** Sliding window considered for failure-rate analysis. */
  recentFailureWindowMs: number;
  /** >= this many failures in the window → degraded. */
  degradedFailureCount: number;
  /** >= this many pending outbox items → degraded. */
  outboxDegradedCount: number;
}

export const DEFAULT_SESSION_HEALTH_THRESHOLDS: SessionHealthThresholds = {
  recentFailureWindowMs: 30_000,
  degradedFailureCount: 3,
  outboxDegradedCount: 1,
};

/**
 * Pure verdict computation. No side effects, no DOM, no network.
 *
 * Decision order (first match wins):
 *   1. Not connected OR never received hello → "offline".
 *   2. >= degradedFailureCount failures within recentFailureWindowMs → "degraded".
 *   3. >= outboxDegradedCount pending outbox items → "degraded".
 *   4. Otherwise → "online".
 */
export function computeSessionHealth(
  input: SessionHealthInput,
  thresholds: SessionHealthThresholds = DEFAULT_SESSION_HEALTH_THRESHOLDS,
): SessionHealthVerdict {
  if (!input.connected || !input.hasHello) {
    return {
      kind: "offline",
      reason: !input.connected
        ? "WebSocket disconnected from gateway at localhost:18789"
        : "Awaiting gateway hello",
    };
  }

  const windowStart = input.nowMs - thresholds.recentFailureWindowMs;
  const failuresInWindow = input.recentFailures.filter((f) => f.at >= windowStart);

  if (failuresInWindow.length >= thresholds.degradedFailureCount) {
    const newest = failuresInWindow[failuresInWindow.length - 1];
    return {
      kind: "degraded",
      reason:
        `${failuresInWindow.length} recent failure(s) within ` +
        `${Math.round(thresholds.recentFailureWindowMs / 1000)}s ` +
        `(latest: ${newest.reason})`,
    };
  }

  if (input.pendingOutboxCount >= thresholds.outboxDegradedCount) {
    return {
      kind: "degraded",
      reason:
        input.pendingOutboxCount === 1
          ? "1 optimistic message awaiting backend confirmation"
          : `${input.pendingOutboxCount} optimistic messages awaiting backend confirmation`,
    };
  }

  return { kind: "online", reason: "Connected to gateway at localhost:18789" };
}

/**
 * Trim a failure buffer to the recent window. Returns a new array
 * (does not mutate). Used by the monitor after each poll so the
 * buffer does not grow unbounded.
 */
export function trimSessionHealthFailures(
  failures: readonly SessionHealthFailure[],
  nowMs: number,
  windowMs: number = DEFAULT_SESSION_HEALTH_THRESHOLDS.recentFailureWindowMs,
): SessionHealthFailure[] {
  const windowStart = nowMs - windowMs;
  // Keep one extra failure BEFORE the window so the next record can
  // still see context; cheap, bounded.
  return failures.filter((f) => f.at >= windowStart);
}

/**
 * Append a failure to the buffer, trimming old entries. Used by the
 * monitor when the host's `lastError` field changes.
 */
export function recordSessionHealthFailure(
  failures: readonly SessionHealthFailure[],
  failure: SessionHealthFailure,
  nowMs: number,
  windowMs: number = DEFAULT_SESSION_HEALTH_THRESHOLDS.recentFailureWindowMs,
): SessionHealthFailure[] {
  const trimmed = trimSessionHealthFailures(failures, nowMs, windowMs);
  return [...trimmed, failure];
}
